/**
 * Legend row under the Explore Map's `<svg>`. v3 (design review): the map is
 * monochrome — every tile is a shade of `palette.primary` (accent on
 * hover), so the legend's swatches are exactly those shades, not an
 * "example hue" stand-in. `COVERAGE_STATUS_LABEL` stays the one vocabulary
 * source for the text. Split out of `ExplorationMap.tsx`
 * (docs/project/code-quality.md file-size guidance); no behaviour of its
 * own beyond the swatches.
 *
 * @module features/exploration/components/ExplorationMapLegend
 */

import { Text, type ChartTheme } from '@chatsdet/ui';
import { COVERAGE_STATUS_LABEL } from '../../product-map/productMapLogic';
import { COVERAGE_ALPHA, withAlpha } from '../map/svgGeometry';

export function ExplorationMapLegend({ palette }: { palette: ChartTheme }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-3">
      <div className="flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: palette.primary }} />
        <Text size="xs" className="text-muted-foreground">
          {COVERAGE_STATUS_LABEL.covered}
        </Text>
      </div>
      <div className="flex items-center gap-1.5">
        <span
          className="relative inline-block h-2.5 w-2.5 rounded-sm"
          style={{ backgroundColor: withAlpha(palette.primary, COVERAGE_ALPHA.stale) }}
        >
          <span
            className="absolute inset-0 m-auto h-1 w-1 rounded-full"
            style={{ backgroundColor: palette.warning }}
          />
        </span>
        <Text size="xs" className="text-muted-foreground">
          {COVERAGE_STATUS_LABEL.stale}
        </Text>
      </div>
      <div className="flex items-center gap-1.5">
        <span
          className="relative inline-block h-2.5 w-2.5 rounded-sm"
          style={{ backgroundColor: withAlpha(palette.primary, COVERAGE_ALPHA.stale) }}
        >
          <span
            className="absolute inset-0 m-auto h-1 w-1 rounded-full"
            style={{ backgroundColor: palette.destructive }}
          />
        </span>
        <Text size="xs" className="text-muted-foreground">
          {COVERAGE_STATUS_LABEL.known_issue}
        </Text>
      </div>
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block h-2.5 w-2.5 rounded-sm"
          style={{ backgroundColor: withAlpha(palette.primary, COVERAGE_ALPHA.uncovered) }}
        />
        <Text size="xs" className="text-muted-foreground">
          {COVERAGE_STATUS_LABEL.uncovered}
        </Text>
      </div>
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block h-2.5 w-2.5 rounded-sm"
          style={{ backgroundColor: withAlpha(palette.neutral, COVERAGE_ALPHA.fogged) }}
        />
        <Text size="xs" className="text-muted-foreground">
          Never explored
        </Text>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-xs" style={{ color: palette.text }}>
          ?
        </span>
        <Text size="xs" className="text-muted-foreground">
          Lead
        </Text>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: palette.text }} />
        <Text size="xs" className="text-muted-foreground">
          Worker
        </Text>
      </div>
    </div>
  );
}
